/**
 * Compress a video in the browser with ffmpeg.wasm (no server upload).
 * Targets ~65% of original file size at the same resolution / fps.
 */
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

const TARGET_RATIO = 0.65;
const AUDIO_BPS = 128_000;
const MIN_VIDEO_BPS = 250_000;
const CORE_BASE = 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/esm';

let ffmpegSingleton = null;
let loadPromise = null;

function formatBitrate(bps) {
  if (bps >= 1_000_000) return `${(bps / 1_000_000).toFixed(2)}M`;
  return `${Math.max(1, Math.round(bps / 1000))}k`;
}

export function probeVideoFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    const cleanup = () => URL.revokeObjectURL(url);
    video.onloadedmetadata = () => {
      const duration = Number(video.duration) || 0;
      const width = Number(video.videoWidth) || 0;
      const height = Number(video.videoHeight) || 0;
      cleanup();
      if (!duration || duration === Infinity) {
        reject(new Error('Could not read video duration.'));
        return;
      }
      resolve({ duration, width, height });
    };
    video.onerror = () => {
      cleanup();
      reject(new Error('Could not read this video file.'));
    };
    video.src = url;
  });
}

async function getFfmpeg(onLog) {
  if (ffmpegSingleton?.loaded) return ffmpegSingleton;
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    const ffmpeg = new FFmpeg();
    if (onLog) {
      ffmpeg.on('log', ({ message }) => onLog(message));
    }
    await ffmpeg.load({
      coreURL: await toBlobURL(`${CORE_BASE}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${CORE_BASE}/ffmpeg-core.wasm`, 'application/wasm'),
    });
    ffmpegSingleton = ffmpeg;
    return ffmpeg;
  })();

  try {
    return await loadPromise;
  } catch (err) {
    loadPromise = null;
    throw err;
  }
}

/**
 * @param {File|Blob} file
 * @param {{ onProgress?: (p: { phase: string, ratio: number, label: string }) => void }} [opts]
 */
export async function compressVideoLocal(file, opts = {}) {
  const onProgress = opts.onProgress || (() => {});
  const originalSize = Number(file.size || 0);
  if (!originalSize) throw new Error('Empty video file');

  onProgress({ phase: 'probe', ratio: 0.02, label: 'Reading video…' });
  const meta = await probeVideoFile(file);
  const duration = Math.max(0.1, meta.duration);
  const targetBytes = Math.max(1, Math.floor(originalSize * TARGET_RATIO));
  const totalBps = Math.floor((targetBytes * 8) / duration);
  const videoBps = Math.max(MIN_VIDEO_BPS, totalBps - AUDIO_BPS);
  const vBitrate = formatBitrate(videoBps);
  const aBitrate = formatBitrate(AUDIO_BPS);
  const maxrate = formatBitrate(Math.floor(videoBps * 1.15));
  const bufsize = formatBitrate(videoBps * 2);

  // Keep even dimensions for yuv420p
  let scaleArgs = [];
  if (meta.width > 0 && meta.height > 0) {
    const w = meta.width % 2 === 0 ? meta.width : meta.width - 1;
    const h = meta.height % 2 === 0 ? meta.height : meta.height - 1;
    if (w !== meta.width || h !== meta.height) {
      scaleArgs = ['-vf', `scale=${w}:${h}`];
    }
  }

  onProgress({ phase: 'load', ratio: 0.08, label: 'Loading compressor (one-time)…' });
  const ffmpeg = await getFfmpeg();

  const progressHandler = ({ progress }) => {
    const ratio = 0.12 + Math.min(0.85, Math.max(0, progress) * 0.85);
    onProgress({
      phase: 'encode',
      ratio,
      label: `Encoding locally… ${Math.round(Math.min(1, Math.max(0, progress)) * 100)}%`,
    });
  };
  ffmpeg.on('progress', progressHandler);

  const inName = 'input.bin';
  const outName = 'output.mp4';
  try {
    onProgress({ phase: 'write', ratio: 0.1, label: 'Preparing video…' });
    await ffmpeg.writeFile(inName, await fetchFile(file));

    // veryfast = much quicker in-browser than server round-trip for large clips
    await ffmpeg.exec([
      '-i',
      inName,
      ...scaleArgs,
      '-c:v',
      'libx264',
      '-preset',
      'veryfast',
      '-b:v',
      vBitrate,
      '-maxrate',
      maxrate,
      '-bufsize',
      bufsize,
      '-c:a',
      'aac',
      '-b:a',
      aBitrate,
      '-movflags',
      '+faststart',
      '-pix_fmt',
      'yuv420p',
      outName,
    ]);

    onProgress({ phase: 'read', ratio: 0.96, label: 'Finalizing…' });
    const data = await ffmpeg.readFile(outName);
    const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
    let blob = new Blob([bytes], { type: 'video/mp4' });
    let compressedSize = blob.size;
    let note = 'ok';
    let achievedRatio = originalSize ? compressedSize / originalSize : 0;

    if (compressedSize >= originalSize) {
      blob = file instanceof Blob ? file : new Blob([file], { type: file.type || 'video/mp4' });
      compressedSize = originalSize;
      achievedRatio = 1;
      note = 'already_efficient';
    }

    const objectUrl = URL.createObjectURL(blob);
    const base = String(file.name || 'video').replace(/\.[^.]+$/, '');
    onProgress({ phase: 'done', ratio: 1, label: 'Done' });

    return {
      blob,
      objectUrl,
      filename: `${base}-65pct.mp4`,
      originalSize,
      compressedSize,
      achievedRatio,
      targetRatio: TARGET_RATIO,
      note,
      mode: 'local',
      meta,
    };
  } finally {
    ffmpeg.off('progress', progressHandler);
    try {
      await ffmpeg.deleteFile(inName);
    } catch {
      /* ignore */
    }
    try {
      await ffmpeg.deleteFile(outName);
    } catch {
      /* ignore */
    }
  }
}
